import { supabase } from '@/integrations/supabase/client';

const BUCKET_NAME = 'email-assets';

// List of icons to upload
const CONTACT_ICONS = ['email', 'phone', 'whatsapp', 'website', 'location'];
const SOCIAL_ICONS = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube', 'tiktok', 'whatsapp'];

/**
 * Check if email assets are already uploaded to Supabase storage
 */
export const checkEmailAssetsExist = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list('icons/contact');
    
    if (error) {
      console.error('Error checking email assets:', error);
      return false;
    }
    
    // If we have at least some icons, assume they're uploaded
    return data && data.length >= CONTACT_ICONS.length;
  } catch (err) {
    console.error('Error checking email assets:', err);
    return false;
  }
};

/**
 * Upload a single icon from public folder to Supabase storage
 */
const uploadIcon = async (
  sourcePath: string, 
  destinationPath: string
): Promise<boolean> => {
  try {
    // Fetch the icon from public folder
    const response = await fetch(sourcePath);
    if (!response.ok) {
      console.error(`Failed to fetch ${sourcePath}: ${response.status}`);
      return false;
    }
    
    const blob = await response.blob();
    
    // Check if file already exists
    const { data: existingFile } = await supabase.storage
      .from(BUCKET_NAME)
      .list(destinationPath.split('/').slice(0, -1).join('/'), {
        search: destinationPath.split('/').pop()
      });
    
    if (existingFile && existingFile.length > 0) {
      console.log(`Icon already exists: ${destinationPath}`);
      return true;
    }
    
    // Upload to Supabase storage
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(destinationPath, blob, {
        contentType: 'image/png',
        upsert: true
      });
    
    if (error) {
      console.error(`Failed to upload ${destinationPath}:`, error);
      return false;
    }
    
    console.log(`Uploaded: ${destinationPath}`);
    return true;
  } catch (err) {
    console.error(`Error uploading ${sourcePath}:`, err);
    return false;
  }
};

/**
 * Upload all email signature icons to Supabase storage
 * This should be called once to migrate icons to public storage
 */
export const uploadEmailAssets = async (): Promise<{ success: boolean; uploaded: number; failed: number }> => {
  let uploaded = 0;
  let failed = 0;
  
  // Upload contact icons
  for (const icon of CONTACT_ICONS) {
    const success = await uploadIcon(
      `/icons/contact/${icon}.png`,
      `icons/contact/${icon}.png`
    );
    if (success) uploaded++; else failed++;
  }
  
  // Upload social icons
  for (const icon of SOCIAL_ICONS) {
    const success = await uploadIcon(
      `/icons/social/${icon}.png`,
      `icons/social/${icon}.png`
    );
    if (success) uploaded++; else failed++;
  }
  
  return {
    success: failed === 0,
    uploaded,
    failed
  };
};

/**
 * Get the public URL for an email asset
 */
export const getEmailAssetPublicUrl = (path: string): string => {
  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(path);
  
  return data.publicUrl;
};
